import { relations } from 'drizzle-orm';
import { index, integer, pgEnum, pgTable, serial, text, timestamp } from 'drizzle-orm/pg-core';
import { type TypeId, typeIdDataType as typeId } from '../utils/typeid';
import { account, accountStatusEnum } from './account';

/**
 * The kind of change recorded in the account history timeline.
 * - `status_change` is a transition of the account status (e.g. running -> errored).
 * - `proxy_swap` is a change of the proxy attached to the account (manual update or automatic rotation).
 * - `account_scope_assignment` is the first and permanent assignment of a Scope.
 */
export const ACCOUNT_HISTORY_STATUS_CHANGE = 'status_change';
export const ACCOUNT_HISTORY_PROXY_SWAP = 'proxy_swap';
export const ACCOUNT_HISTORY_ACCOUNT_SCOPE_ASSIGNMENT = 'account_scope_assignment';

export const ACCOUNT_HISTORY_TYPE = [
  ACCOUNT_HISTORY_STATUS_CHANGE,
  ACCOUNT_HISTORY_PROXY_SWAP,
  ACCOUNT_HISTORY_ACCOUNT_SCOPE_ASSIGNMENT,
] as const;

export type AccountHistoryType = (typeof ACCOUNT_HISTORY_TYPE)[number];

export const accountHistoryTypeEnum = pgEnum('account_history_type', ACCOUNT_HISTORY_TYPE);

/**
 * User-facing history of the important changes that affected an account (status changes and proxy swaps).
 *
 * This is an independent source of truth for the account detail timeline: entries are written from the
 * same business locations as webhooks but stored separately, so the history exists regardless of whether
 * any webhook was configured or delivered.
 */
export const accountHistory = pgTable(
  'account_history',
  {
    id: serial().primaryKey(),
    public_id: typeId('accountHistory', 'public_id').notNull(),
    account_id: integer()
      .references(() => account.id, { onDelete: 'cascade' })
      .notNull(),
    created_at: timestamp({ withTimezone: true, mode: 'string' }).notNull().defaultNow(),
    type: accountHistoryTypeEnum().notNull(),
    // Populated for `status_change` entries.
    new_status: accountStatusEnum(),
    // Populated for `proxy_swap` entries. Human-readable proxy labels (e.g. `proxy.example.com:8080 (FR)`
    // or `Automatic (FR)`). Never store proxy credentials here.
    new_proxy: text(),
    // Populated for `account_scope_assignment` entries.
    new_account_scope_id: text().$type<TypeId<'accountScope'>>(),
    // Optional human-readable reason for the change, when available (e.g. `proxy`, `provider`, `unipile`).
    reason: text(),
    // Populated for `status_change` entries when the transition is scoped to a specific product (e.g. auth error on `gmail`).
    product: text(),
  },
  (t) => [index('account_history_account_id_created_at_idx').on(t.account_id, t.created_at)],
);

export const accountHistoryRelations = relations(accountHistory, ({ one }) => ({
  account: one(account, { fields: [accountHistory.account_id], references: [account.id] }),
}));
